import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  AU_STATES,
  CONTACT_ROLES,
  CONTACT_ROLE_LABEL,
  can,
  formatPhoneAu,
  type ClientContact,
  type ClientOrgDetails,
  type ContactRole,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadClients, newId, removeContact, saveClientOrg, saveContact, type Clients } from './api.ts';

export interface ClientsScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
}

const emptyOrg = (tenantId: string): ClientOrgDetails => ({
  id: newId(),
  tenant_id: tenantId,
  name: '',
  legal_name: '',
  company_no: '',
  address_line1: '',
  address_line2: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'Australia',
  email: '',
  web: '',
  deleted_at: null,
});

const emptyContact = (tenantId: string, orgId: string): ClientContact => ({
  id: newId(),
  tenant_id: tenantId,
  client_org_id: orgId,
  name: '',
  position: '',
  email: '',
  phone: '',
  mobile: '',
  roles: ['principal'],
  deleted_at: null,
});

/**
 * Clients and contacts (TJR inventory 01 §2.3): the client organisation's legal name, company number and address once
 * per client, reused on every quote; contacts with their position and roles (principal, notices, billing, field).
 * Tenant admins and managers edit; other staff read.
 */
export function ClientsScreen({ sb, tenantId, userId, roles }: ClientsScreenProps): ReactElement {
  const [data, setData] = useState<Clients | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [org, setOrg] = useState<ClientOrgDetails | null>(null);
  const [contact, setContact] = useState<ClientContact | null>(null);
  const write = can(roles, 'clients.write');

  const reload = useCallback(() => {
    setError(null);
    loadClients(sb, tenantId)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  async function run(action: () => Promise<void>, done: string): Promise<void> {
    try {
      await action();
      setNotice({ ok: true, text: done });
      setOrg(null);
      setContact(null);
    } catch (e: unknown) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
    reload();
  }

  return (
    <section className="page">
      <PageHeader
        title="Clients"
        subtitle="Client organisations and their contacts, used on quotes and contracts."
      >
        {write && (
          <Button variant="primary" onClick={() => setOrg(emptyOrg(tenantId))} disabled={!data}>
            Add client
          </Button>
        )}
      </PageHeader>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {org && (
        <OrgForm
          org={org}
          onChange={setOrg}
          onCancel={() => setOrg(null)}
          onSave={() => {
            if (!org.name.trim()) {
              setNotice({ ok: false, text: 'Enter the client name as printed on the front page.' });
              return;
            }
            void run(() => saveClientOrg(sb, org, userId), `${org.name} saved.`);
          }}
        />
      )}
      {contact && (
        <ContactForm
          contact={contact}
          onChange={setContact}
          onCancel={() => setContact(null)}
          onSave={() => {
            if (!contact.name.trim()) {
              setNotice({ ok: false, text: "Enter the contact's name." });
              return;
            }
            void run(() => saveContact(sb, contact, userId), `${contact.name} saved.`);
          }}
        />
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!data && !error && <Loading what="clients" />}
      {data && data.orgs.length === 0 && (
        <div className="panel">
          <p className="body">No clients yet.{write ? ' Press "Add client" to add the first one.' : ''}</p>
        </div>
      )}
      {data &&
        data.orgs.map((o) => {
          const people = data.contacts.filter((c) => c.client_org_id === o.id);
          return (
            <article key={o.id} className="panel stack" aria-label={o.name}>
              <div className="row row--between">
                <h2 className="heading">{o.name}</h2>
                {write && (
                  <div className="row">
                    <Button variant="quiet" onClick={() => setOrg(o)} aria-label={`Edit ${o.name}`}>
                      Edit
                    </Button>
                    <Button
                      variant="quiet"
                      onClick={() => setContact(emptyContact(tenantId, o.id))}
                      aria-label={`Add contact to ${o.name}`}
                    >
                      Add contact
                    </Button>
                  </div>
                )}
              </div>
              <p className="body muted">
                {[
                  o.legal_name && o.legal_name !== o.name ? o.legal_name : '',
                  o.company_no ? `ACN/ABN ${o.company_no}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <p className="body">
                {[o.address_line1, o.address_line2, o.suburb, o.state, o.postcode, o.country]
                  .filter(Boolean)
                  .join(', ') || 'No address yet.'}
              </p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="label">Contact</th>
                      <th className="label">Position</th>
                      <th className="label">Email</th>
                      <th className="label">Mobile</th>
                      <th className="label">Roles</th>
                      <th className="label">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {people.length === 0 && (
                      <tr>
                        <td colSpan={6} className="muted">
                          No contacts yet.
                        </td>
                      </tr>
                    )}
                    {people.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td>{c.position || '–'}</td>
                        <td className="data">{c.email || '–'}</td>
                        <td className="data">{c.mobile.trim() ? formatPhoneAu(c.mobile.trim()) : '–'}</td>
                        <td>{c.roles.map((r) => CONTACT_ROLE_LABEL[r]).join(', ')}</td>
                        <td className="table__actions">
                          {write && (
                            <>
                              <Button
                                variant="quiet"
                                onClick={() => setContact(c)}
                                aria-label={`Edit ${c.name}`}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="quiet"
                                aria-label={`Remove ${c.name}`}
                                onClick={() =>
                                  void run(() => removeContact(sb, c.id, userId), `${c.name} removed.`)
                                }
                              >
                                Remove
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          );
        })}
    </section>
  );
}

function OrgForm({
  org,
  onChange,
  onSave,
  onCancel,
}: {
  readonly org: ClientOrgDetails;
  readonly onChange: (o: ClientOrgDetails) => void;
  readonly onSave: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  const set = <K extends keyof ClientOrgDetails>(k: K, v: ClientOrgDetails[K]): void =>
    onChange({ ...org, [k]: v });
  return (
    <form
      className="panel stack"
      aria-label="Client details"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <h2 className="heading">Client details</h2>
      <div className="form-grid">
        <TextField label="Name on the front page" value={org.name} onChange={(v) => set('name', v)} />
        <TextField
          label="Company name for the agreement"
          hint="Leave empty when it is the same as the name."
          value={org.legal_name}
          onChange={(v) => set('legal_name', v)}
        />
        <TextField
          label="ACN or ABN"
          spellCheck={false}
          value={org.company_no}
          onChange={(v) => set('company_no', v)}
        />
        <TextField
          label="Address line 1"
          value={org.address_line1}
          onChange={(v) => set('address_line1', v)}
        />
        <TextField
          label="Address line 2"
          value={org.address_line2}
          onChange={(v) => set('address_line2', v)}
        />
        <TextField label="Suburb or city" value={org.suburb} onChange={(v) => set('suburb', v)} />
        <SelectField
          label="State"
          value={org.state}
          onChange={(v) => set('state', v)}
          options={[['', 'Not in Australia'], ...AU_STATES.map((s) => [s.name, s.name] as const)]}
        />
        <TextField
          label="Postcode"
          spellCheck={false}
          value={org.postcode}
          onChange={(v) => set('postcode', v)}
        />
        <TextField label="Country" value={org.country} onChange={(v) => set('country', v)} />
        <TextField label="Email" type="email" value={org.email} onChange={(v) => set('email', v)} />
        <TextField label="Web site" spellCheck={false} value={org.web} onChange={(v) => set('web', v)} />
      </div>
      <div className="row">
        <Button type="submit" variant="primary">
          Save client
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ContactForm({
  contact,
  onChange,
  onSave,
  onCancel,
}: {
  readonly contact: ClientContact;
  readonly onChange: (c: ClientContact) => void;
  readonly onSave: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  const set = <K extends keyof ClientContact>(k: K, v: ClientContact[K]): void =>
    onChange({ ...contact, [k]: v });
  const toggle = (r: ContactRole, on: boolean): void =>
    set('roles', on ? [...contact.roles, r] : contact.roles.filter((x) => x !== r));
  return (
    <form
      className="panel stack"
      aria-label="Contact details"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <h2 className="heading">Contact</h2>
      <div className="form-grid">
        <TextField label="Name" value={contact.name} onChange={(v) => set('name', v)} />
        <TextField label="Position" value={contact.position} onChange={(v) => set('position', v)} />
        <TextField label="Email" type="email" value={contact.email} onChange={(v) => set('email', v)} />
        <TextField label="Phone" type="tel" value={contact.phone} onChange={(v) => set('phone', v)} />
        <TextField label="Mobile" type="tel" value={contact.mobile} onChange={(v) => set('mobile', v)} />
      </div>
      <fieldset className="fieldset stack">
        <legend className="label muted">Roles</legend>
        {CONTACT_ROLES.map((r) => (
          <CheckField
            key={r}
            label={CONTACT_ROLE_LABEL[r]}
            checked={contact.roles.includes(r)}
            onChange={(on) => toggle(r, on)}
          />
        ))}
      </fieldset>
      <div className="row">
        <Button type="submit" variant="primary">
          Save contact
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
